/**
 * Predict 充值 Hook
 *
 * 功能说明：
 * - 从 EOA 钱包转账 Polygon USDC.e 到 Safe 钱包
 * - 纯前端实现，使用 Privy sendTransaction 静默签名
 * - 开启 Privy gas 赞助，用户无需 MATIC 支付 gas 费
 *
 * 资金流向：
 * EOA 钱包 Polygon USDC.e → Safe 钱包
 */
'use client';

import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useWallets, useSendTransaction } from '@privy-io/react-auth';
import { encodeFunctionData, erc20Abi, parseUnits } from 'viem';
import { baseRequest } from '@/lib/dex-api';
import { getApiBaseUrl } from '@/lib/api';
import { formatPriceWithoutComma } from '@/app/perps/util';
import { safeSendTransaction } from '@/lib/privy-utils';
import { CONTRACTS } from '@/lib/predict-approvals';
import { buildWrapTxs } from '@/lib/predict-wrap';
import { executeSafeTransactions } from '@/lib/predict-relayer';
import { usePredictStore } from '@/app/stores/usePredictStore';
import { useSilentSigner } from './use-ethers5-signer';

// ============ 常量配置 ============

/** Polygon USDC.e 合约地址 */
const POLYGON_USDC_E_ADDRESS = CONTRACTS.usdc;

/** Polygon 链 ID */
export const POLYGON_CHAIN_ID = 137;

/** USDC 小数位数 */
const USDC_DECIMALS = CONTRACTS.usdcDecimals;

/** 最小充值金额（USDC）：测试环境 1，生产环境 5 */
export const MIN_DEPOSIT_AMOUNT = process.env.NODE_ENV === 'production' ? 5 : 1;

// ============ Hook 返回类型 ============

export interface UsePredictDepositReturn {
  /** 现货 Polygon USDC.e 余额（格式化后） */
  spotBalance: string;
  /** 现货 Polygon USDC.e 余额原始值 */
  spotBalanceRaw: number;
  /** 是否正在加载余额 */
  isLoadingBalance: boolean;
  /** 是否正在充值 */
  isDepositing: boolean;
  /** 错误信息 */
  error: string | null;
  /** 刷新余额 */
  refreshBalance: () => Promise<void>;
  /** 执行充值；skipValidate 为 true 时跳过 Hook 内与 spotBalanceRaw 的校验（由调用方已按 token 展示余额校验） */
  deposit: (amount: string, options?: { skipValidate?: boolean }) => Promise<DepositResult>;
  /** 验证充值金额 */
  validateAmount: (amount: string) => ValidationResult;
}

/** 充值结果 */
export interface DepositResult {
  success: boolean;
  txHash?: string;
  error?: string;
}

/** 验证结果 */
export interface ValidationResult {
  valid: boolean;
  error?: string;
}

/** 现货充值目标链与代币（不传则默认 Polygon USDC.e） */
export interface PredictDepositTarget {
  chainId: number;
  tokenContractAddress: string;
  decimals: number;
}

// ============ Hook 实现 ============

/**
 * Predict 充值 Hook
 *
 * @param evmAddress - 用户 EVM 钱包地址
 * @param safeAddress - 用户 Safe 钱包地址
 * @param depositTarget - 可选：从哪条 EVM 链、哪个代币转入 Safe；默认 Polygon USDC.e
 * @returns {UsePredictDepositReturn} 充值相关状态和方法
 */
export function usePredictDeposit(
  evmAddress: string | undefined | null,
  safeAddress: string | undefined | null,
  depositTarget?: PredictDepositTarget | null
): UsePredictDepositReturn {
  // ============ Hooks ============
  const { t } = useTranslation();
  const { wallets } = useWallets();
  const { sendTransaction } = useSendTransaction();
  const silentSigner = useSilentSigner();

  const target = useMemo<PredictDepositTarget>(
    () =>
      depositTarget?.tokenContractAddress
        ? depositTarget
        : {
            chainId: POLYGON_CHAIN_ID,
            tokenContractAddress: POLYGON_USDC_E_ADDRESS,
            decimals: USDC_DECIMALS,
          },
    [depositTarget?.chainId, depositTarget?.tokenContractAddress, depositTarget?.decimals]
  );

  // 根据 evmAddress 匹配对应的钱包（严格匹配，不使用 fallback）
  const evmWallet = evmAddress ? wallets.find(w => w.address?.toLowerCase() === evmAddress.toLowerCase()) : null;

  // ============ 状态 ============
  const [spotBalance, setSpotBalance] = useState<string>('0.00');
  const [spotBalanceRaw, setSpotBalanceRaw] = useState<number>(0);
  const [isLoadingBalance, setIsLoadingBalance] = useState(false);
  const [isDepositing, setIsDepositing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ============ 获取现货余额 ============

  /** 刷新所选链上目标代币余额 */
  const refreshBalance = useCallback(async () => {
    if (!evmAddress) {
      setSpotBalance('0.00');
      setSpotBalanceRaw(0);
      return;
    }

    setIsLoadingBalance(true);
    setError(null);

    try {
      const baseUrl = getApiBaseUrl();
      const res = await baseRequest(`${baseUrl}/app/dex/balance/list-token-balances`, {
        params: {
          chains: target.chainId.toString(),
          address: evmAddress,
        },
      });

      const tokenAssets = res?.[0]?.tokenAssets || [];
      const match = tokenAssets.find(
        (token: { tokenContractAddress: string }) =>
          token.tokenContractAddress?.toLowerCase() === target.tokenContractAddress.toLowerCase()
      );

      const rawBalance = parseFloat(match?.balance || '0');
      setSpotBalanceRaw(rawBalance);
      setSpotBalance(
        formatPriceWithoutComma({
          price: rawBalance,
          decimal: Math.min(target.decimals, 8),
          defaultZero: '0.00',
        })
      );
    } catch (err) {
      console.error('[usePredictDeposit] 获取现货余额失败:', err);
      setError(t('predict.deposit.error.getBalanceFailed'));
      setSpotBalance('0.00');
      setSpotBalanceRaw(0);
    } finally {
      setIsLoadingBalance(false);
    }
  }, [evmAddress, target.chainId, target.tokenContractAddress, target.decimals, t]);

  // ============ 验证金额 ============

  /** 验证充值金额是否有效 */
  const validateAmount = useCallback(
    (amount: string): ValidationResult => {
      const numAmount = parseFloat(amount);

      if (!amount || isNaN(numAmount)) {
        return { valid: false, error: t('predict.deposit.error.invalidAmount') };
      }

      if (numAmount <= 0) {
        return { valid: false, error: t('predict.deposit.error.amountMustBePositive') };
      }

      if (numAmount < MIN_DEPOSIT_AMOUNT) {
        return { valid: false, error: t('predict.deposit.error.minAmount', { amount: MIN_DEPOSIT_AMOUNT }) };
      }

      if (numAmount > spotBalanceRaw) {
        return { valid: false, error: t('predict.deposit.error.insufficientBalance') };
      }

      return { valid: true };
    },
    [spotBalanceRaw, t]
  );

  // ============ 执行充值 ============

  /**
   * 执行充值：EOA 钱包 Polygon USDC.e → Safe 钱包
   * 纯前端实现，使用 Privy sendTransaction 静默签名
   */
  const deposit = useCallback(
    async (amount: string, options?: { skipValidate?: boolean }): Promise<DepositResult> => {
      if (!evmAddress || !safeAddress) {
        return { success: false, error: t('predict.deposit.error.walletNotConnected') };
      }

      if (!evmWallet) {
        return { success: false, error: t('predict.deposit.error.walletNotReady') };
      }

      if (!options?.skipValidate) {
        const validation = validateAmount(amount);
        if (!validation.valid) {
          return { success: false, error: validation.error };
        }
      }

      setIsDepositing(true);
      setError(null);

      try {
        await evmWallet.switchChain(target.chainId);

        const amountWei = parseUnits(amount, target.decimals);
        const transferData = encodeFunctionData({
          abi: erc20Abi,
          functionName: 'transfer',
          args: [safeAddress as `0x${string}`, amountWei],
        });

        const tokenAddr = target.tokenContractAddress as `0x${string}`;

        const result = await safeSendTransaction(() =>
          sendTransaction(
            {
              to: tokenAddr,
              data: transferData,
              chainId: target.chainId,
            },
            {
              sponsor: true, // 开启 gas 赞助，用户无需 MATIC
              uiOptions: {
                showWalletUIs: false,
              },
            }
          )
        );

        console.log('[usePredictDeposit] 转账成功, txHash:', result.hash);

        // V2 环境下追加 Safe 内 approve(USDC.e, Onramp) + wrap(USDC.e, Safe, amount)
        // V1 环境保持原行为(只 transfer 不 wrap)
        const version = usePredictStore.getState().version ?? 1;
        if (version === 2 && silentSigner && safeAddress) {
          try {
            console.log('[usePredictDeposit] V2 环境,追加 wrap USDC.e → pUSD');
            const wrapTxs = buildWrapTxs(amountWei, safeAddress);
            const wrapResult = await executeSafeTransactions(silentSigner, wrapTxs, 'Deposit wrap USDC.e → pUSD');
            console.log('[usePredictDeposit] Wrap 成功, txHash:', wrapResult.transactionHash);
          } catch (wrapErr) {
            // wrap 失败不阻塞充值成功提示,仅 console
            console.error('[usePredictDeposit] Wrap 失败(不阻塞):', wrapErr);
          }
        }

        // 4. 刷新余额
        await refreshBalance();

        return { success: true, txHash: result.hash };
      } catch (err: unknown) {
        console.error('[usePredictDeposit] 充值失败:', err);

        // 判断是否为用户取消
        const rawErrorMessage = err instanceof Error ? err.message : '';
        if (rawErrorMessage.includes('rejected') || rawErrorMessage.includes('cancelled') || rawErrorMessage.includes('denied')) {
          const msg = t('predict.deposit.error.userCancelled');
          setError(msg);
          return { success: false, error: msg };
        }

        // 判断余额不足（如果 gas 赞助失败，可能会出现此错误）
        if (rawErrorMessage.includes('insufficient funds')) {
          const msg = t('predict.deposit.error.gasSponsorFailed');
          setError(msg);
          return { success: false, error: msg };
        }

        const fallbackMsg = t('predict.deposit.error.depositFailed');
        setError(fallbackMsg);
        return { success: false, error: fallbackMsg };
      } finally {
        setIsDepositing(false);
      }
    },
    [evmAddress, safeAddress, evmWallet, validateAmount, sendTransaction, refreshBalance, silentSigner, t, target.chainId, target.tokenContractAddress, target.decimals]
  );

  // ============ 返回值 ============

  return {
    spotBalance,
    spotBalanceRaw,
    isLoadingBalance,
    isDepositing,
    error,
    refreshBalance,
    deposit,
    validateAmount,
  };
}
